<script setup>
// Seletor de contatos para "enviar contato" na conversa (estilo WhatsApp).
// Permite escolher UM ou VÁRIOS contatos e monta o texto nos DOIS formatos que o
// ContactShare.vue já sabe reconhecer:
//   um:     👤 *Contato: <nome>*
//           📞 <telefone>
//   vários: 👤 *<N> contatos compartilhados*
//           • <nome> — <telefone>
// O marcador 👤 é o que faz o Message.vue rotear para a bolha de cartão.
import { ref, computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const emit = defineEmits(['select']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const searchRef = ref(null);
const query = ref('');
const isSearching = ref(false);
// mantém os escolhidos mesmo quando a busca troca a lista exibida
const picked = ref([]);

const contacts = computed(() => store.getters['contacts/getContacts'] || []);

// só faz sentido oferecer quem tem telefone: o cartão é nome + número
const results = computed(() =>
  contacts.value.filter(contact => contact.phone_number)
);

const isPicked = id => picked.value.some(contact => contact.id === id);

const toggle = contact => {
  picked.value = isPicked(contact.id)
    ? picked.value.filter(item => item.id !== contact.id)
    : [...picked.value, contact];
};

const search = async () => {
  const term = query.value.trim();
  isSearching.value = true;
  try {
    if (term) {
      await store.dispatch('contacts/search', {
        search: encodeURIComponent(term),
        page: 1,
      });
    } else {
      await store.dispatch('contacts/get', { page: 1 });
    }
  } finally {
    isSearching.value = false;
  }
};

const open = async () => {
  query.value = '';
  picked.value = [];
  dialogRef.value?.open();
  search();
  await nextTick();
  searchRef.value?.focus();
};

const confirm = () => {
  if (!picked.value.length) return;
  // manda os contatos crus: quem monta o vCard e o texto do cartão é o backend,
  // que envia pelo AstraCall e registra a mensagem já com o source_id.
  emit(
    'select',
    picked.value.map(contact => ({
      name: contact.name,
      phone: contact.phone_number,
    }))
  );
  dialogRef.value?.close();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONVERSATION.REPLYBOX.SHARE_CONTACT.TITLE')"
    :confirm-button-label="t('CONVERSATION.REPLYBOX.SHARE_CONTACT.CONFIRM')"
    :cancel-button-label="t('CONVERSATION.REPLYBOX.SHARE_CONTACT.CANCEL')"
    :disable-confirm-button="!picked.length"
    width="md"
    @confirm="confirm"
  >
    <div class="flex flex-col gap-3">
      <input
        ref="searchRef"
        v-model="query"
        type="text"
        class="px-3 py-2 w-full text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
        :placeholder="t('CONVERSATION.REPLYBOX.SHARE_CONTACT.SEARCH')"
        @input="search"
      />
      <div
        v-if="picked.length"
        class="flex justify-between items-center text-xs text-n-slate-11"
      >
        <span>
          {{
            t('CONVERSATION.REPLYBOX.SHARE_CONTACT.SELECTED', {
              count: picked.length,
            })
          }}
        </span>
        <button
          type="button"
          class="font-medium text-n-brand hover:underline"
          @click="picked = []"
        >
          {{ t('CONVERSATION.REPLYBOX.SHARE_CONTACT.CLEAR') }}
        </button>
      </div>
      <div class="overflow-y-auto max-h-72">
        <p v-if="isSearching" class="py-3 text-sm text-n-slate-11">
          {{ t('CONVERSATION.REPLYBOX.SHARE_CONTACT.LOADING') }}
        </p>
        <p v-else-if="!results.length" class="py-3 text-sm text-n-slate-11">
          {{ t('CONVERSATION.REPLYBOX.SHARE_CONTACT.EMPTY') }}
        </p>
        <div v-else class="flex flex-col">
          <button
            v-for="contact in results"
            :key="contact.id"
            type="button"
            class="flex gap-3 items-center px-3 py-2 w-full text-left rounded-lg hover:bg-n-alpha-1"
            @click="toggle(contact)"
          >
            <span
              class="flex justify-center items-center rounded border size-4 shrink-0"
              :class="
                isPicked(contact.id)
                  ? 'bg-n-brand border-n-brand'
                  : 'border-n-strong'
              "
            >
              <span
                v-if="isPicked(contact.id)"
                class="text-white i-lucide-check size-3"
              />
            </span>
            <span class="flex flex-col gap-0.5 min-w-0">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ contact.name }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ contact.phone_number }}
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </Dialog>
</template>
